import { RodeoEventRegistration } from "model/rodeoEventRegistration";
import { PayoutConfig } from "../model/payoutConfig";
import { Rodeo } from "../model/rodeo";
import { RodeoEvent } from "../model/rodeoEvent";
import { RodeoEventRun } from "../model/rodeoEventRun";
import { PayoutSource } from "model/payout-source";
import { Leader } from "./types/leader";
import { ChangeRequestHelper } from "../model/registration-change-request";
import { Winner } from "model/winner";
import { newGuid } from "@microsoft/applicationinsights-core-js";

export class LeaderGenerator {
    private static _instance: LeaderGenerator;

    private constructor() { }

    static instance(): LeaderGenerator {
        if (this._instance == undefined)
            this._instance = new this();

        return this._instance;
    }

    getAllLeaders(rodeo: Rodeo, payoutConfigs: PayoutConfig[], showPenaltyRunsOverride: boolean = false): Map<number, any[]> {
        let allLeaderGroups: Map<number, any[]> = new Map();
        payoutConfigs.forEach((config: PayoutConfig) => {
            let key = config.id;

            if (!key || key == 0)
                key = payoutConfigs.indexOf(config);

            allLeaderGroups.set(key, this.getLeaders(rodeo, config, 9999, config.includePenalties, false, false, showPenaltyRunsOverride));
        });

        return allLeaderGroups;
    }

    getRunsOn(onRuns: number, byRunIdentifier: boolean, byScore: boolean, fromTotalTimeScore: boolean, runs: RodeoEventRun[]) {
        let sourceRunsToAdd = [];
        var groupedRuns: Map<string, any[]> = new Map<string, any[]>();

        // Group runs into fastestRuns
        runs.forEach((run) => {
            const runKey = byRunIdentifier ? run.runIdentifier : `${run.contestantOneId}_${run.horseOneId}_${run.contestantTwoId}_${run.horseTwoId}`;

            if (!groupedRuns.has(runKey))
                groupedRuns.set(runKey, []);

            groupedRuns.get(runKey).push(run);
        });

        // Clear sort fastestRuns, add to runToAdd
        groupedRuns.forEach((runSet) => {
            runSet.sort((a, b) => {
                if ((a.noTime || a.turnedOut) && !b.noTime && !b.turnedOut)
                    return 1;
                else if ((b.noTime || b.turnedOut) && !a.noTime && !a.turnedOut)
                    return -1;

                if (fromTotalTimeScore) {
                    if (byScore) {
                        if (+a.totalScore > +b.totalScore)
                            return 1;
                        else if (+a.totalScore < +b.totalScore)
                            return -1;
                        else
                            return 0;
                    } else {
                        if (+a.totalTime > +b.totalTime)
                            return 1;
                        else if (+a.totalTime < +b.totalTime)
                            return -1;
                        else
                            return 0;
                    }
                } else {
                    if (byScore) {
                        if (+a.score > +b.score)
                            return 1;
                        else if (+a.score < +b.score)
                            return -1;
                        else
                            return 0;
                    } else {
                        if (+a.time > +b.time)
                            return 1;
                        else if (+a.time < +b.time)
                            return -1;
                        else
                            return 0;
                    }
                }
            });

            runSet = runSet.filter((x, index) => index < onRuns);
            sourceRunsToAdd.push(...runSet);
        });

        return sourceRunsToAdd;
    }

    getAveragedRuns(byRunIdentifier: boolean, byScore: boolean, decimalPlaces: number, runs: RodeoEventRun[], fromTotalTime: boolean, aggregate: boolean) {
        let sourceRunsToAdd = JSON.parse(JSON.stringify(runs));
        let successfulRuns = [];

        sourceRunsToAdd.forEach(run => {
            // Since we're copying data off original runs, update the runIdentifier
            const runKey = byRunIdentifier ? run.runIdentifier : `${run.contestantOneId}_${run.horseOneId}_${run.contestantTwoId}_${run.horseTwoId}`;
            run.runIdentifier = runKey;

            if (!successfulRuns[run.runIdentifier])
                successfulRuns[run.runIdentifier] = 0;

            if (run.done && !run.noTime && !run.turnedOut) {
                successfulRuns[run.runIdentifier]++;

                if (fromTotalTime) {
                    successfulRuns[run.runIdentifier]++;
                }
            }
        });

        let lastRunsBySchedule = new Map<string, RodeoEventRun>();
        sourceRunsToAdd.forEach(run => {
            run.displaySuccessfulRuns = null;

            if (!run.done)
                return;

            if (run.noTime || run.turnedOut || run.notifiedTurnoutCount) {
                run.time = 0;
                run.score = 0;
            }

            run.livestock = { brand: '', name: '' };

            if (!lastRunsBySchedule.has(run.runIdentifier)) {
                run.displaySuccessfulRuns = successfulRuns[run.runIdentifier];
                run.totalTime = fromTotalTime ? run.totalTime : run.time;
                run.totalScore = fromTotalTime ? run.totalScore : run.score;
                run.penaltyTime = run.penaltyTime;

                lastRunsBySchedule.set(run.runIdentifier, run);
            } else {
                lastRunsBySchedule.get(run.runIdentifier).totalScore += fromTotalTime ? run.totalScore : run.score;
                lastRunsBySchedule.get(run.runIdentifier).isExtraRunForContestantOne = lastRunsBySchedule.get(run.runIdentifier).isExtraRunForContestantOne || run.isExtraRunForContestantOne;
                lastRunsBySchedule.get(run.runIdentifier).isExtraRunForContestantTwo = lastRunsBySchedule.get(run.runIdentifier).isExtraRunForContestantTwo || run.isExtraRunForContestantTwo;
                lastRunsBySchedule.get(run.runIdentifier).totalTime += fromTotalTime ? run.totalTime : run.time;
                lastRunsBySchedule.get(run.runIdentifier).round = Math.max(+run.round, +lastRunsBySchedule.get(run.runIdentifier).round);
                lastRunsBySchedule.get(run.runIdentifier).penaltyTime += run.penaltyTime;
                lastRunsBySchedule.get(run.runIdentifier).turnedOut = lastRunsBySchedule.get(run.runIdentifier).turnedOut || run.turnedOut;
                lastRunsBySchedule.get(run.runIdentifier).noTime = lastRunsBySchedule.get(run.runIdentifier).noTime || run.noTime;

                if (successfulRuns[run.runIdentifier] > 0) {
                    lastRunsBySchedule.get(run.runIdentifier).noTime = false;
                    lastRunsBySchedule.get(run.runIdentifier).turnedOut = false;
                }
            }
        }, {});

        sourceRunsToAdd = [];

        lastRunsBySchedule.forEach(run => {
            if (successfulRuns[run.runIdentifier] > 0)
                sourceRunsToAdd.push(run);
        });

        if (!aggregate) {
            sourceRunsToAdd.forEach((run) => {
                if (byScore)
                    run.totalScore = +(+(+run.totalScore / Math.max(1, successfulRuns[run.runIdentifier]))).toFixed(decimalPlaces);
                else
                    run.totalTime = +(+(+run.totalTime / Math.max(1, successfulRuns[run.runIdentifier]))).toFixed(decimalPlaces);
            });
        } else {
            sourceRunsToAdd.forEach((run) => {
                if (byScore)
                    run.totalScore = +(+run.totalScore).toFixed(decimalPlaces);
                else
                    run.totalTime = +(+run.totalTime).toFixed(decimalPlaces);
            });
        }

        sourceRunsToAdd.sort((a, b) => {
            if (successfulRuns[a.runIdentifier] > successfulRuns[b.runIdentifier])
                return -1;
            else if (successfulRuns[a.runIdentifier] < successfulRuns[b.runIdentifier])
                return 1;

            // Add negative to have uniform sorting by greater value (greatest -time is better, greatest score is better)
            let aCompare = -1 * +a.totalTime;
            let bCompare = -1 * +b.totalTime;

            if (byScore) {
                aCompare = +a.totalScore;
                bCompare = +b.totalScore;
            }

            if (aCompare != 0 && bCompare == 0)
                return -1;
            else if (aCompare == 0 && bCompare != 0)
                return 1;

            if (aCompare > bCompare)
                return -1;
            else if (aCompare < bCompare)
                return 1;
            else
                return 0;
        });

        return sourceRunsToAdd;
    }

    getMoneyWonRuns(rodeoEvent: RodeoEvent) {
        var winnerMap = new Map<string, Winner>();
        var totalMap = new Map<string, number>();

        rodeoEvent.winners.forEach((winner) => {
            const winnerKey = `${winner.contestantOne ? winner.contestantOne.id : 'undefined'}_${winner.contestantTwo ? winner.contestantTwo.id : 'undefined'}_${winner.horseOne ? winner.horseOne.id : 'undefined'}_${winner.horseTwo ? winner.horseTwo.id : 'undefined'}`;
            if (!winnerMap.has(winnerKey))
                winnerMap.set(winnerKey, winner);

            if (!totalMap.has(winnerKey))
                totalMap.set(winnerKey, 0);

            totalMap.set(winnerKey, totalMap.get(winnerKey) + winner.payout);
        })

        var runs: RodeoEventRun[] = [];

        winnerMap.forEach((x, key) => {
            runs.push(Object.assign(new RodeoEventRun(), {
                number: x.number,
                runIdentifier: newGuid(),
                contestantOne: x.contestantOne,
                contestantOneId: x.contestantOneId,
                contestantTwo: x.contestantTwo,
                contestantTwoId: x.contestantTwoId,
                horseOne: x.horseOne,
                horseOneId: x.horseOneId,
                horseTwo: x.horseTwo,
                horseTwoId: x.horseTwoId,
                time: totalMap.get(key),
                score: totalMap.get(key),
                done: true
            }));
        })

        return runs;
    }

    getLeaders(rodeo: Rodeo, mode: PayoutConfig, maxPlaces: number, includePenalties: boolean, includeTurnoutsAndNoTimes: boolean = false, fillPlaces: boolean = false, showPenaltyRunsOverride: boolean = false): Leader[] {
        let leaderGroups: Leader[] = [];

        if (!rodeo || !mode)
            return leaderGroups;

        let runs: RodeoEventRun[] = [];
        let byScore = false;
        let decimalPlaces = mode.isMoneyWon ? 2 : 1;
        let runsNotFinished = 0;

        mode.sources.sort((a, b) => {
            if (a.addToCount && !b.addToCount)
                return -1;
            else if (!a.addToCount && b.addToCount)
                return 1;

            return 0;
        })

        mode.sources.forEach((source) => {
            let sourceRunsToAdd: RodeoEventRun[] = [];
            var rodeoEvent: RodeoEvent = null;

            if (source.rodeoEventId) {
                rodeoEvent = rodeo.rodeoEvents.filter(x => x.id == source.rodeoEventId)[0];
                byScore = rodeoEvent.configuration.runs.scoreValue;
                decimalPlaces = mode.isMoneyWon ? 2 : rodeoEvent.configuration.runs.decimalPlaces;
            }

            if (!rodeoEvent)
                return;

            var allRuns = rodeoEvent.schedules.flatMap(x => x.runs);
            var carryOverNonMatchingRounds = [];

            rodeo.rodeoEvents.filter(x => x.carryOvers.some(y => y.carryOverId == rodeoEvent.id)).forEach((sourceCarryOverEvent) => {
                sourceCarryOverEvent.carryOvers.filter(x => x.carryOverId == rodeoEvent.id).forEach((carryOverConfig) => {
                    sourceCarryOverEvent.registrations.filter(x => x.carryOvers.some(y => y.rodeoEventId == rodeoEvent.id)).forEach((carryOverRegistration) => {
                        var matchingRuns = sourceCarryOverEvent.schedules.flatMap(x => x.runs).filter(x =>
                            (carryOverRegistration.contestantOneId == x.contestantOneId || (carryOverRegistration.teammateHeader && carryOverRegistration.contestantOneId == x.contestantTwoId))
                            && (carryOverRegistration.horseOneId == x.horseOneId || (carryOverRegistration.teammateHeader && carryOverRegistration.horseOneId == x.horseTwoId))
                            && (carryOverRegistration.contestantTwoId == x.contestantTwoId || (carryOverRegistration.teammateHeader && carryOverRegistration.contestantTwoId == x.contestantOneId))
                            && (carryOverRegistration.horseTwoId == x.horseTwoId || (carryOverRegistration.teammateHeader && carryOverRegistration.horseTwoId == x.horseOneId))
                            && (carryOverConfig.sourceRound == 0 || carryOverConfig.sourceRound == x.round)
                            && (carryOverConfig.destinationRound == 0 || source.round == 0 || carryOverConfig.destinationRound == source.round));

                        if (matchingRuns) {
                            allRuns.push(...matchingRuns);

                            if (source.round != 0) {
                                carryOverNonMatchingRounds.push(...matchingRuns.filter(x => x.round != source.round && (carryOverConfig.sourceRound != carryOverConfig.destinationRound)));
                            }
                        }
                    });
                })
            });

            if (source.scheduleId) {
                sourceRunsToAdd = allRuns.filter(x => x.rodeoEventScheduleId == source.scheduleId);
            } else if (source.sidepotId) {
                sourceRunsToAdd = [];

                rodeo.rodeoEvents.forEach((rodeoEvent) => {
                    var registrations = rodeoEvent.registrations.filter(x => x.sidepotIds.some(y => y == source.sidepotId));

                    if (registrations.length > 0) {
                        sourceRunsToAdd.push(...allRuns.filter(x =>
                            registrations.some(y =>
                                (y.contestantOneId == x.contestantOneId || (y.teammateHeader && y.contestantOneId == x.contestantTwoId))
                                && (y.horseOneId == x.horseOneId || (y.teammateHeader && y.horseOneId == x.horseTwoId))
                                && (y.contestantTwoId == x.contestantTwoId || (y.teammateHeader && y.contestantTwoId == x.contestantOneId))
                                && (y.horseTwoId == x.horseTwoId || (y.teammateHeader && y.horseTwoId == x.horseOneId)))
                        ));
                    }
                })
            } else {
                sourceRunsToAdd = allRuns;
            }

            if (mode.isMoneyWon) {
                sourceRunsToAdd = this.getMoneyWonRuns(rodeoEvent);
            } else {
                // Filter out non-matching rounds
                if (source.round != 0) {
                    sourceRunsToAdd = sourceRunsToAdd.filter(x => x.round == source.round || carryOverNonMatchingRounds.some(y => y == x));
                } else if (source.onXRounds && source.onXRounds != 0) {
                    sourceRunsToAdd = this.getRunsOn(source.onXRounds, true, byScore, source.fromTotalTimeScore, sourceRunsToAdd);
                }

                if (source.isAggregate || source.isAverage) {
                    sourceRunsToAdd = this.getAveragedRuns(source.byRunIdentifier, byScore, decimalPlaces, sourceRunsToAdd, source.fromTotalTimeScore, source.isAggregate);
                    sourceRunsToAdd.forEach((run) => {
                        run.time = run.totalTime;
                        run.score = run.totalScore;
                    })
                }

                if (!source.addToCount) {
                    sourceRunsToAdd = sourceRunsToAdd.filter(x => runs.some(y =>
                        (y.contestantOneId == x.contestantOneId)
                        && (y.horseOneId == x.horseOneId)
                        && (y.contestantTwoId == x.contestantTwoId)
                        && (y.horseTwoId == x.horseTwoId)));
                }
            }

            /* Exclude ones that are extra?
            if (!mode.contestantOnePayout || !mode.contestantTwoPayout) {
                if (mode.contestantOnePayout && !mode.contestantTwoPayout)
                    sourceRunsToAdd = sourceRunsToAdd.filter(x => !x.isExtraRunForContestantOne);
                else if (mode.contestantTwoPayout && !mode.contestantOnePayout)
                    sourceRunsToAdd = sourceRunsToAdd.filter(x => !x.isExtraRunForContestantTwo);
            }
            */

            runs.push(...sourceRunsToAdd);
        });

        if (mode.isMoneyWon) {
            var winnerMap = new Map<string, RodeoEventRun>();
            var totalMap = new Map<string, number>();

            runs.forEach((run) => {
                const winnerKey = `${run.contestantOne ? run.contestantOne.id : 'undefined'}_${run.contestantTwo ? run.contestantTwo.id : 'undefined'}_${run.horseOne ? run.horseOne.id : 'undefined'}_${run.horseTwo ? run.horseTwo.id : 'undefined'}`;
                if (!winnerMap.has(winnerKey))
                    winnerMap.set(winnerKey, run);

                if (!totalMap.has(winnerKey))
                    totalMap.set(winnerKey, 0);

                totalMap.set(winnerKey, totalMap.get(winnerKey) + run.score);
            })

            runs = [];

            winnerMap.forEach((x, key) => {
                runs.push(Object.assign(new RodeoEventRun(), {
                    id: x.id,
                    runIdentifier: x.runIdentifier,
                    number: x.number,
                    contestantOne: x.contestantOne,
                    contestantOneId: x.contestantOneId,
                    contestantTwo: x.contestantTwo,
                    contestantTwoId: x.contestantTwoId,
                    horseOne: x.horseOne,
                    horseOneId: x.horseOneId,
                    horseTwo: x.horseTwo,
                    horseTwoId: x.horseTwoId,
                    time: totalMap.get(key),
                    score: totalMap.get(key),
                    done: true
                }));
            });
        }

        runs = [...new Set(runs)];

        runs = runs.filter(x => x.done);
        runsNotFinished += runs.filter(x => !x.done).length;

        if (!mode.isMoneyWon) {
            // Filter out slowest runs
            if (mode.onXRounds != 0) {
                runs = this.getRunsOn(mode.onXRounds, false, byScore, false, runs);
            }

            if (mode.isAggregate || mode.isAverage) {
                runs = this.getAveragedRuns(mode.byRunIdentifier, byScore, decimalPlaces, runs, mode.sources.length == 1 ? mode.sources[0].fromTotalTimeScore : false, mode.isAggregate);
            }

            if (!showPenaltyRunsOverride) {
                if (!includePenalties)
                    runs = runs.filter(x => x.penaltyTime <= 0);
    
                if (!includeTurnoutsAndNoTimes)
                    runs = runs.filter(x => !x.turnedOut);
    
                if (!includeTurnoutsAndNoTimes && !fillPlaces)
                    runs = runs.filter(x => !x.noTime);
            }
        } else {
            byScore = true;
        }

        if (byScore) {
            if (!mode.isAggregate && !mode.isAverage) {
                runs.sort((a, b) => {
                    if ((a.noTime || a.turnedOut) && !b.noTime && !b.turnedOut)
                        return 1;
                    else if ((b.noTime || b.turnedOut) && !a.noTime && !a.turnedOut)
                        return -1;

                    if (+a.score < +b.score)
                        return 1;
                    else if (+a.score > +b.score)
                        return -1;
                    else if (+a.time < +b.time)
                        return 1;
                    else if (+a.time > +b.time)
                        return -1;
                    else
                        return 0;
                });
            }
        } else {
            if (mode.name.includes("Barrel One Split") || mode.name.includes("Home Split")) {
                runs.sort((a, b) => {
                    if ((a.noTime || a.turnedOut) && !b.noTime && !b.turnedOut)
                        return 1;
                    else if ((b.noTime || b.turnedOut) && !a.noTime && !a.turnedOut)
                        return -1;
                    + +a.aroundBarrelOneSplit
                    if (mode.name.includes("Barrel One Split")) {
                        if ((!a.straightOneSplit || !a.aroundBarrelOneSplit) && (!b.straightOneSplit || !b.aroundBarrelOneSplit))
                            return 0;
                        else if ((!a.straightOneSplit || !a.aroundBarrelOneSplit) && b.straightOneSplit && b.aroundBarrelOneSplit)
                            return 1;
                        else if (a.straightOneSplit && a.aroundBarrelOneSplit && (!b.straightOneSplit || !b.aroundBarrelOneSplit))
                            return -1;
                        if (+a.straightOneSplit + +a.aroundBarrelOneSplit > +b.straightOneSplit + +b.aroundBarrelOneSplit)
                            return 1;
                        else if (+a.straightOneSplit + +a.aroundBarrelOneSplit < +b.straightOneSplit + +b.aroundBarrelOneSplit)
                            return -1;
                    } else {
                        if (!a.straightFourSplit && !b.straightFourSplit)
                            return 0;
                        else if (!a.straightFourSplit && b.straightFourSplit)
                            return 1;
                        else if (a.straightFourSplit && !b.straightFourSplit)
                            return -1;
                        if (+a.straightFourSplit > +b.straightFourSplit)
                            return 1;
                        else if (+a.straightFourSplit < +b.straightFourSplit)
                            return -1;
                    }

                    return 0;
                });
            } else {
                if (!mode.isAggregate && !mode.isAverage) {
                    runs.sort((a, b) => {
                        if ((a.noTime || a.turnedOut) && !b.noTime && !b.turnedOut)
                            return 1;
                        else if ((b.noTime || b.turnedOut) && !a.noTime && !a.turnedOut)
                            return -1;

                        /*
                        if (a.displaySuccessfulRuns < b.displaySuccessfulRuns)
                            return 1;
                        else if (b.displaySuccessfulRuns < a.displaySuccessfulRuns)
                            return -1;
                        */

                        if (+a.time > +b.time)
                            return 1;
                        else if (+a.time < +b.time)
                            return -1;
                        else
                            return 0;
                    });
                }
            }
        }

        if (mode.uniqueRunsOnly) {
            let runsProcessed = [];
            let runsToRemove = [];
            runs.forEach((run) => {
                if (runsProcessed.some(x => x.runIdentifier == run.runIdentifier)) {
                    runsToRemove.push(run);
                } else {
                    runsProcessed.push(run);
                }
            })

            runs = runs.filter(x => !runsToRemove.some(y => y.id == x.id));
        }

        let currentDivision = 0;

        //console.log((mode.isAggregate ? 'Aggregate' : mode.isAverage ? 'Average' : '') + " runs: ", runs);
        //console.log('maxPlaces: ', maxPlaces);
        //console.log('fillPlaces: ', fillPlaces);        

        //console.log('mode: ' + mode.name + 'runs: ', runs);

        if (runs.length > 0) {
            let displayAllRuns = maxPlaces >= runs.length;
            let runIndex = 0;
            let ignoreCutoff = false;
            let startTime = +(+((mode.isAggregate || mode.isAverage) ? runs[0].totalTime : runs[0].time)).toFixed(decimalPlaces);
            let cutoffTime = startTime + +mode.divisionOffsetArray[0];
            let maxSuccessfulRuns = runs[runIndex].displaySuccessfulRuns;

            if (mode.isAggregate)
                cutoffTime += +mode.divisionOffsetArray[0];

            if (mode.maxDivisions == 1)
                cutoffTime = 500;

            //console.log('startTime: ', startTime);
            //console.log('cutoffTime: ', cutoffTime);

            while (currentDivision < mode.maxDivisions) {
                if (mode.maxDivisions - currentDivision == 1) {
                    ignoreCutoff = true;
                }

                let leaderRuns: RodeoEventRun[] = [];

                while (runIndex < runs.length && (mode.name.includes("Bottom's Up") || leaderRuns.length < maxPlaces)) {
                    if (mode.isAverage || mode.isAggregate) {
                        //if (runs[runIndex].displaySuccessfulRuns != startRound && !fillPlaces)
                        //    break;

                        if (byScore) {
                            leaderRuns.push(runs[runIndex]);
                        } else {
                            //console.log('checking if totalTime > startTime for ', runs[runIndex]);
                            if ((+runs[runIndex].totalTime >= startTime || fillPlaces || runs[runIndex].displaySuccessfulRuns < maxSuccessfulRuns) || (ignoreCutoff && (runs[runIndex].noTime || runs[runIndex].turnedOut))) {
                                if (+runs[runIndex].totalTime >= cutoffTime && !ignoreCutoff)
                                    break;

                                //console.log('add leader run ', runs[runIndex]);
                                leaderRuns.push(runs[runIndex]);
                            }
                        }
                    } else {
                        if (byScore || mode.name.includes("Barrel One Split") || mode.name.includes("Home Split")) {
                            leaderRuns.push(runs[runIndex]);
                        } else {
                            if (+runs[runIndex].time >= startTime || (ignoreCutoff && (runs[runIndex].noTime || runs[runIndex].turnedOut))) {

                                if (+runs[runIndex].time >= cutoffTime && !ignoreCutoff) {
                                    break;
                                }

                                leaderRuns.push(runs[runIndex]);
                            }
                        }
                    }

                    runIndex++;
                }

                while (runIndex > 0 && runs.length > 1 && runs.length > runIndex && (mode.name.includes("Bottom's Up") || leaderRuns.length >= maxPlaces)) {
                    if (mode.isAverage || mode.isAggregate) {
                        if (byScore) {
                            if (+(+runs[runIndex].totalScore).toFixed(decimalPlaces) == +(+runs[runIndex - 1].totalScore).toFixed(decimalPlaces)) {
                                leaderRuns.push(runs[runIndex]);
                            } else {
                                break;
                            }
                        } else {
                            if (+(+runs[runIndex].totalTime).toFixed(decimalPlaces) == +(+runs[runIndex - 1].totalTime).toFixed(decimalPlaces)) {
                                leaderRuns.push(runs[runIndex]);
                            } else {
                                break;
                            }
                        }
                    } else {
                        if (byScore) {
                            if (+(+runs[runIndex].score).toFixed(decimalPlaces) == +(+runs[runIndex - 1].score).toFixed(decimalPlaces)) {
                                leaderRuns.push(runs[runIndex]);
                            } else {
                                break;
                            }
                        } else {
                            if (+(runs[runIndex].time.toFixed(decimalPlaces)) == +(runs[runIndex - 1].time.toFixed(decimalPlaces))) {
                                leaderRuns.push(runs[runIndex]);
                            } else {
                                break;
                            }
                        }
                    }

                    runIndex++;
                }

                let leaderGroup: any = {};
                leaderGroup.runsNotFinished = runsNotFinished;
                leaderGroup
                leaderGroup.mode = mode;
                leaderGroup.name = mode.name + (mode.maxDivisions > 1 ? ' - ' + (currentDivision + 1) + "D" : "");
                leaderGroup.startPlace = 1;

                if (mode.name.includes("Bottom's Up") && !displayAllRuns) {
                    let uniqueTimeCount = 0;
                    let lastTime = 0;
                    let runsToSlice = 0;

                    for (let i = leaderRuns.length - 1; i > 0 && uniqueTimeCount <= maxPlaces; i--) {
                        if (leaderRuns[i].time != lastTime) {
                            uniqueTimeCount++;
                            lastTime = leaderRuns[i].time;
                        }

                        if (uniqueTimeCount <= maxPlaces) {
                            runsToSlice++;
                            leaderGroup.startPlace = leaderRuns.length - runsToSlice + 1;
                        }
                    }

                    leaderGroup.runs = leaderRuns.slice(-runsToSlice);
                } else
                    leaderGroup.runs = leaderRuns;

                if (mode.maxDivisions > 1)
                    leaderGroup.divisions = true;
                else
                    leaderGroup.divisions = false;

                if (mode.maxDivisions > 1) {
                    leaderGroup.startTime = startTime;
                }

                leaderGroups.push(leaderGroup);
                startTime = cutoffTime;

                if (mode.maxDivisions - currentDivision > 1) {
                    cutoffTime += +mode.divisionOffsetArray[currentDivision + 1];

                    if (mode.isAggregate)
                        cutoffTime += +mode.divisionOffsetArray[currentDivision + 1];

                    cutoffTime = +(+cutoffTime).toFixed(decimalPlaces);
                }

                currentDivision++;
            }
        } else {
            while (currentDivision < mode.maxDivisions) {
                let leaderRuns: RodeoEventRun[] = [];
                let leaderGroup: any = {};

                leaderGroup.runsNotFinished = runsNotFinished;
                leaderGroup.mode = mode;
                leaderGroup.name = mode.name + (mode.maxDivisions > 1 ? ' - ' + (currentDivision + 1) + "D" : "");
                leaderGroup.runs = leaderRuns;

                if (mode.maxDivisions > 1)
                    leaderGroup.divisions = true;
                else
                    leaderGroup.divisions = false;

                leaderGroups.push(leaderGroup);
                currentDivision++;
            }
        }

        return leaderGroups;
    }

    getMembershipDiscountTotal(rodeo, mode: PayoutConfig, excludeNotifiedTurnouts: boolean) {
        var discountAmount = 0;
        var dateRequirementForDiscount: Date;

        mode.sources.forEach((source) => {
            if (source.scheduleId || source.sidepotId || source.amountToPot == 0 || !source.addToCount)
                return;

            let runs: RodeoEventRun[] = [];
            let sourceRegistrationsToAdd: RodeoEventRegistration[] = [];
            var rodeoEvent: RodeoEvent = null;

            if (source.rodeoEventId) {
                rodeoEvent = rodeo.rodeoEvents.filter(x => x.id == source.rodeoEventId)[0];
            }

            runs = rodeoEvent.schedules.flatMap(x => x.runs);
            sourceRegistrationsToAdd = rodeoEvent.registrations.filter(x => x.id > 0);
            var carryOverNonMatchingRounds = [];

            rodeo.rodeoEvents.filter(x => x.carryOvers.some(y => y.carryOverId == rodeoEvent.id)).forEach((sourceCarryOverEvent) => {
                sourceCarryOverEvent.carryOvers.filter(x => x.carryOverId == rodeoEvent.id).forEach((carryOverConfig) => {
                    sourceCarryOverEvent.registrations.filter(x => x.carryOvers.some(y => y.rodeoEventId == rodeoEvent.id)).forEach((carryOverRegistration) => {
                        var matchingRuns = sourceCarryOverEvent.schedules.flatMap(x => x.runs).filter(x =>
                            (carryOverRegistration.contestantOneId == x.contestantOneId || (carryOverRegistration.teammateHeader && carryOverRegistration.contestantOneId == x.contestantTwoId))
                            && (carryOverRegistration.horseOneId == x.horseOneId || (carryOverRegistration.teammateHeader && carryOverRegistration.horseOneId == x.horseTwoId))
                            && (carryOverRegistration.contestantTwoId == x.contestantTwoId || (carryOverRegistration.teammateHeader && carryOverRegistration.contestantTwoId == x.contestantOneId))
                            && (carryOverRegistration.horseTwoId == x.horseTwoId || (carryOverRegistration.teammateHeader && carryOverRegistration.horseTwoId == x.horseOneId))
                            && (carryOverConfig.sourceRound == 0 || carryOverConfig.sourceRound == x.round)
                            && (carryOverConfig.destinationRound == 0 || source.round == 0 || carryOverConfig.destinationRound == source.round));

                        sourceRegistrationsToAdd.push(carryOverRegistration);

                        if (matchingRuns) {
                            runs.push(...matchingRuns);

                            if (source.round != 0) {
                                carryOverNonMatchingRounds.push(...matchingRuns.filter(x => x.round != source.round && (carryOverConfig.sourceRound != carryOverConfig.destinationRound)));
                            }
                        }
                    });
                })
            });

            if (excludeNotifiedTurnouts) {
                var registrationsOutOfPot = sourceRegistrationsToAdd
                    .filter(x => x.changeRequests.some(y => ChangeRequestHelper.isMoneyOutOfPot(y))).map(x => x.runIdentifier);
                var notifiedTurnoutRuns = [...new Set(runs.filter(x => registrationsOutOfPot.includes(x.runIdentifier)))];
                runs = runs.filter(x => !notifiedTurnoutRuns.some(y => y.runIdentifier == x.runIdentifier));
            }

            runs.map(x => x.contestantOne).concat(runs.filter(x => x.contestantTwo).map(x => x.contestantTwo)).forEach((contestant) => {
                var memberships = rodeo.organization.memberships.filter(x => contestant.memberships.some(y => y.membershipId == x.id))
                var maxDiscount = 0;

                memberships.forEach((membership) => {
                    if (membership.discounts) {
                        var membershipStartDate = new Date(contestant.memberships.filter(x => x.membershipId == membership.id)[0].activated);

                        if (membershipStartDate > dateRequirementForDiscount)
                            return;

                        membership.discounts.forEach((discount) => {
                            if (new Date() > new Date(discount.activeTo) || !discount.isActive)
                                return;

                            var contestantDiscount = discount.isPercent ? rodeoEvent.priceTiers[0].unitPrice * (discount.discountAmount / 100) : discount.discountAmount;

                            if (contestantDiscount > maxDiscount)
                                maxDiscount = contestantDiscount;
                        })
                    }
                })

                discountAmount += maxDiscount;
            })

            if (!dateRequirementForDiscount)
                dateRequirementForDiscount = new Date(rodeoEvent.schedules[0].scheduledDate);
        });

        return discountAmount;
    }

    getTotalCount(rodeo: Rodeo, mode: PayoutConfig, source: PayoutSource, excludeNotifiedTurnouts: boolean, alsoCheckRuns: boolean = false): number {
        let runs: RodeoEventRun[] = [];
        let registrations: RodeoEventRegistration[] = [];
        let byScore = false;
        let decimalPlaces = 1;

        if (!source.addToCount)
            return 0;

        let sourceRunsToAdd: RodeoEventRun[] = [];
        let sourceRegistrationsToAdd: RodeoEventRegistration[] = [];
        var rodeoEvent: RodeoEvent = null;

        if (source.rodeoEventId) {
            rodeoEvent = rodeo.rodeoEvents.filter(x => x.id == source.rodeoEventId)[0];
            byScore = rodeoEvent.configuration.runs.scoreValue;
            decimalPlaces = rodeoEvent.configuration.runs.decimalPlaces;
        }

        var carryOverNonMatchingRounds = [];

        if (source.scheduleId) {
            sourceRunsToAdd = rodeoEvent.schedules.filter(x => x.id == source.scheduleId)[0].runs;
            sourceRegistrationsToAdd = rodeoEvent.registrations.filter(x => x.id > 0);
        } else if (source.sidepotId) {
            sourceRunsToAdd = [];

            rodeo.rodeoEvents.forEach((rodeoEvent) => {
                var registrations = rodeoEvent.registrations.filter(x => x.sidepotIds.some(y => y == source.sidepotId));

                if (registrations.length > 0) {
                    sourceRunsToAdd.push(...rodeoEvent.schedules.flatMap(x => x.runs).filter(x =>
                        registrations.some(y =>
                            (y.contestantOneId == x.contestantOneId || (y.teammateHeader && y.contestantOneId == x.contestantTwoId))
                            && (y.horseOneId == x.horseOneId || (y.teammateHeader && y.horseOneId == x.horseTwoId))
                            && (y.contestantTwoId == x.contestantTwoId || (y.teammateHeader && y.contestantTwoId == x.contestantOneId))
                            && (y.horseTwoId == x.horseTwoId || (y.teammateHeader && y.horseTwoId == x.horseOneId)))
                    ));

                    sourceRegistrationsToAdd.push(...registrations);
                }
            })
        } else {
            sourceRunsToAdd = rodeoEvent.schedules.flatMap(x => x.runs);
            sourceRegistrationsToAdd = rodeoEvent.registrations.filter(x => x.id > 0);

            rodeo.rodeoEvents.filter(x => x.carryOvers.some(y => y.carryOverId == rodeoEvent.id)).forEach((sourceCarryOverEvent) => {
                sourceCarryOverEvent.carryOvers.filter(x => x.carryOverId == rodeoEvent.id).forEach((carryOverConfig) => {
                    sourceCarryOverEvent.registrations.filter(x => x.carryOvers.some(y => y.rodeoEventId == rodeoEvent.id)).forEach((carryOverRegistration) => {
                        var matchingRuns = sourceCarryOverEvent.schedules.flatMap(x => x.runs).filter(x =>
                            (carryOverRegistration.contestantOneId == x.contestantOneId || (carryOverRegistration.teammateHeader && carryOverRegistration.contestantOneId == x.contestantTwoId))
                            && (carryOverRegistration.horseOneId == x.horseOneId || (carryOverRegistration.teammateHeader && carryOverRegistration.horseOneId == x.horseTwoId))
                            && (carryOverRegistration.contestantTwoId == x.contestantTwoId || (carryOverRegistration.teammateHeader && carryOverRegistration.contestantTwoId == x.contestantOneId))
                            && (carryOverRegistration.horseTwoId == x.horseTwoId || (carryOverRegistration.teammateHeader && carryOverRegistration.horseTwoId == x.horseOneId))
                            && (carryOverConfig.sourceRound == 0 || carryOverConfig.sourceRound == x.round)
                            && (carryOverConfig.destinationRound == 0 || source.round == 0 || carryOverConfig.destinationRound == source.round));

                        sourceRegistrationsToAdd.push(carryOverRegistration);

                        if (matchingRuns) {
                            sourceRunsToAdd.push(...matchingRuns);

                            if (source.round != 0) {
                                carryOverNonMatchingRounds.push(...matchingRuns.filter(x => x.round != source.round && (carryOverConfig.sourceRound != carryOverConfig.destinationRound)));
                            }
                        }
                    });
                })
            });
        }

        // Filter out non-matching rounds
        if (source.round != 0) {
            sourceRunsToAdd = sourceRunsToAdd.filter(x => x.round == source.round || carryOverNonMatchingRounds.some(y => y == x));
        }

        // Filter out slowest runs
        if (source.onXRounds && source.onXRounds != 0) {
            sourceRunsToAdd = this.getRunsOn(source.onXRounds, true, byScore, source.fromTotalTimeScore, sourceRunsToAdd);
        }

        if (source.isAggregate || source.isAverage) {
            sourceRunsToAdd = this.getAveragedRuns(source.byRunIdentifier, byScore, decimalPlaces, sourceRunsToAdd, source.fromTotalTimeScore, source.isAggregate);
        }

        if (mode.contestantOnePayout && !mode.contestantTwoPayout)
            sourceRegistrationsToAdd = sourceRegistrationsToAdd.filter(x => !x.isDraw || (!x.teammateHeader && x.contestantOne != null) || (x.teammateHeader && x.contestantTwo !=null));
        else if (mode.contestantTwoPayout && !mode.contestantOnePayout)
            sourceRegistrationsToAdd = sourceRegistrationsToAdd.filter(x => !x.isDraw || (!x.teammateHeader && x.contestantTwo != null) || (x.teammateHeader && x.contestantOne !=null));

        var notifiedTurnoutCount = 0;
        if (excludeNotifiedTurnouts)
            notifiedTurnoutCount = sourceRegistrationsToAdd
                .filter(x => x.changeRequests.some(y => ChangeRequestHelper.isMoneyOutOfPot(y))).length;

        runs.push(...sourceRunsToAdd);
        registrations.push(...sourceRegistrationsToAdd);

        let count = registrations.length;
        if (alsoCheckRuns) {
            count = count > 0 ? count : runs.length;
        }

        return count - notifiedTurnoutCount;
    }
}


// WEBPACK FOOTER //
// ./src/lib/leaderGenerator.ts